import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MARKETING_NAME } from '../../support/brand';
import {
  CLOSING,
  CTAS,
  DEFINITION,
  DIFFERENTIATORS,
  DIFFERENTIATORS_SECTION,
  FAQS,
  FAQ_SECTION,
  HERO,
  LAST_MODIFIED_LABEL,
  PROBLEM,
  REGISTER_URL,
  RELATED_LINKS,
  RESPONSIBILITIES,
  RESPONSIBILITIES_SECTION,
  SEO,
  STEPS,
  STEPS_SECTION,
  WHATSAPP_URL,
} from './content';
import { META_TAGS, STRUCTURED_DATA } from './seo';

const CHAT_CASES = [
  {
    id: 'saude',
    label: 'Saúde',
    emoji: '🩺',
    title: 'Mentoria digital · saúde',
    messages: [
      { from: 'user', text: 'Dormi mal de novo. Tomo café extra ou sigo o protocolo?' },
      {
        from: 'mentor',
        text: 'No seu método: sono ruim não se resolve com mais estímulo. Corte o café depois das 14h, 10 min de luz natural agora e check-in amanhã cedo sobre a noite.',
      },
      { from: 'user', text: 'E se repetir a terceira noite?' },
      {
        from: 'mentor',
        text: 'Aí a gente escala: registro de horário + gatilho. Te cobro amanhã às 7h30. Se passar de 3 noites, indico conversar com seu médico — eu não diagnostico.',
      },
    ],
  },
  {
    id: 'personal',
    label: 'Personal',
    emoji: '💪',
    title: 'Mentoria digital · personal',
    messages: [
      { from: 'user', text: 'Treino de perna hoje ou descanso? Viagem ontem, dormi 5h.' },
      {
        from: 'mentor',
        text: 'Pelo seu método: prioridade é recuperação. Troca perna por mobilidade 25 min + caminhada. Volume pesado fica pra amanhã com sono ≥7h.',
      },
      { from: 'user', text: 'Ok. Me lembra da proteína?' },
      {
        from: 'mentor',
        text: 'Meta do dia: 1,6g/kg. Te mando o check-in às 20h com o que faltou fechar.',
      },
    ],
  },
  {
    id: 'financas',
    label: 'Finanças',
    emoji: '📊',
    title: 'Mentoria digital · finanças',
    messages: [
      { from: 'user', text: 'Consigo investir R$ 800 esse mês sem desmontar a reserva?' },
      {
        from: 'mentor',
        text: 'Pelo seu método: primeiro a margem de segurança. Com a reserva em 4 meses de custo, R$ 800 cabe — desde que não toque no fundo de emergência. Monto o plano da semana?',
      },
      { from: 'user', text: 'Sim. E me cobre na sexta.' },
      {
        from: 'mentor',
        text: 'Feito. Check-in sexta às 9h com o marco da semana. Se desviar, eu puxo você de volta ao plano.',
      },
    ],
  },
  {
    id: 'concursos',
    label: 'Concursos',
    emoji: '📚',
    title: 'Mentoria digital · concursos',
    messages: [
      { from: 'user', text: 'Errando direito administrativo demais. Mudo o material?' },
      {
        from: 'mentor',
        text: 'No método: erro em bloco não é troca de fonte — é ciclo de revisão. Hoje: 40 questões só do tema fraco + anotar o padrão do erro. Material novo só se o padrão persistir 7 dias.',
      },
      { from: 'user', text: 'E o simulado de domingo?' },
      {
        from: 'mentor',
        text: 'Mantém. Domingos são prova; semana é correção. Te lembro sábado à noite do horário e do checklist.',
      },
    ],
  },
  {
    id: 'idiomas',
    label: 'Idiomas',
    emoji: '🌍',
    title: 'Mentoria digital · idiomas',
    messages: [
      { from: 'user', text: 'Travei no speaking de novo na call. Desisto do nível?' },
      {
        from: 'mentor',
        text: 'Pelo método: trava ≠ nível errado. Hoje 12 min de shadowing no áudio da call + 5 frases prontas pro próximo encontro. Não sobe nem desce de nível esta semana.',
      },
      { from: 'user', text: 'Me cobra o drill?' },
      {
        from: 'mentor',
        text: 'Áudio seu até 21h. Eu devolvo feedback em 3 pontos: ritmo, clareza e uma correção só — pra não sobrecarregar.',
      },
    ],
  },
  {
    id: 'carreira',
    label: 'Carreira',
    emoji: '🎯',
    title: 'Mentoria digital · carreira',
    messages: [
      { from: 'user', text: 'Recebi convite pra entrevista. Ainda não me sinto pronto.' },
      {
        from: 'mentor',
        text: 'No seu framework: “pronto” é preparação, não sensação. Hoje: 3 histórias STAR do seu portfólio + uma pergunta que você faz pra eles. Ensaio de 20 min amanhã.',
      },
      { from: 'user', text: 'E se perguntarem salário?' },
      {
        from: 'mentor',
        text: 'Âncora que você já definiu: faixa + trade-offs. Te mando o roteiro amanhã cedo e um lembrete 1h antes da call.',
      },
    ],
  },
];

const MESSAGE_REVEAL_MS = 850;
const HOLD_AFTER_CHAT_MS = 2800;

function Headline({ children, emphasis }) {
  return (
    <>
      {`${children} `}
      <em className="maia-em">{emphasis}</em>
    </>
  );
}

function LandingCtas({ isAuthenticated, align = 'center' }) {
  const alignment =
    align === 'start'
      ? 'items-center sm:items-start lg:justify-start'
      : 'items-center justify-center';

  if (isAuthenticated) {
    return (
      <div className={`flex flex-col ${alignment}`}>
        <Link to="/agents" className="maia-btn-primary">
          {CTAS.panel}
        </Link>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${alignment}`}>
      <div className="flex flex-col items-center gap-3 sm:flex-row">
        <a
          href={WHATSAPP_URL}
          className="maia-btn-primary"
          target="_blank"
          rel="noreferrer"
        >
          {CTAS.contact}
        </a>
        <a href={REGISTER_URL} className="maia-btn-secondary">
          {CTAS.register}
        </a>
      </div>
      {align === 'start' ? (
        <a href="#produto" className="maia-btn-link">
          {CTAS.understand}
        </a>
      ) : null}
    </div>
  );
}

function slideOffset(index, activeIndex, total) {
  let delta = index - activeIndex;
  if (delta > total / 2) delta -= total;
  if (delta < -total / 2) delta += total;
  return delta;
}

function ChatMock({ chatCase, visibleCount, revealing }) {
  const shown = chatCase.messages.slice(0, visibleCount);
  const waitingForNext = revealing && visibleCount < chatCase.messages.length;
  const nextIsMentor =
    waitingForNext && chatCase.messages[visibleCount]?.from === 'mentor';

  return (
    <div className="maia-chat relative h-full w-full overflow-hidden p-5 sm:p-6">
      <div className="mb-5 flex items-center gap-3 border-b border-white/10 pb-4">
        <div className="maia-case-avatar" aria-hidden>
          {chatCase.emoji}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{chatCase.title}</p>
          <p className="truncate text-xs text-white/50">
            {`Assistente treinada no método · por ${MARKETING_NAME}`}
          </p>
        </div>
      </div>

      <div className="maia-chat-messages flex min-h-[17.5rem] flex-col justify-end space-y-3 text-[0.8125rem] leading-relaxed sm:min-h-[18.5rem] sm:text-sm">
        {shown.map((message, index) => (
          <div
            key={`${chatCase.id}-${index}`}
            className={
              message.from === 'user'
                ? 'maia-bubble-out maia-bubble-enter ml-8 px-3.5 py-2.5'
                : 'maia-bubble-in maia-bubble-enter mr-6 px-3.5 py-2.5'
            }
          >
            {message.text}
          </div>
        ))}

        {(waitingForNext || (!revealing && visibleCount >= chatCase.messages.length)) && (
          <div className="flex items-center gap-1.5 px-1 pt-1 text-white/40">
            <span className="maia-typing inline-flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
            </span>
            <span className="text-xs">
              {waitingForNext
                ? nextIsMentor
                  ? 'assistente digitando…'
                  : 'assinante digitando…'
                : 'acompanhando…'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CaseShowcase({ revealAllMessages = false }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(
    revealAllMessages ? CHAT_CASES[0].messages.length : 0
  );
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const total = CHAT_CASES.length;
  const activeCase = CHAT_CASES[activeIndex];

  const goTo = (index) => {
    setActiveIndex((index + total) % total);
    setVisibleCount(0);
    setProgress(0);
  };

  useEffect(() => {
    if (paused) return undefined;

    if (visibleCount < activeCase.messages.length) {
      const timer = window.setTimeout(() => {
        setVisibleCount((count) => count + 1);
      }, visibleCount === 0 ? 320 : MESSAGE_REVEAL_MS);
      return () => window.clearTimeout(timer);
    }

    const started = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - started;
      setProgress(Math.min(1, elapsed / HOLD_AFTER_CHAT_MS));
      if (elapsed >= HOLD_AFTER_CHAT_MS) {
        window.clearInterval(tick);
        setActiveIndex((current) => (current + 1) % total);
        setVisibleCount(0);
        setProgress(0);
      }
    }, 40);

    return () => window.clearInterval(tick);
  }, [activeIndex, visibleCount, paused, activeCase.messages.length, total]);

  return (
    <div
      className="mx-auto w-full max-w-lg"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
    >
      <div className="mb-4 flex flex-wrap justify-center gap-2">
        {CHAT_CASES.map((chatCase, index) => (
          <button
            key={chatCase.id}
            type="button"
            onClick={() => goTo(index)}
            className={`maia-case-pill ${index === activeIndex ? 'is-active' : ''}`}
            aria-pressed={index === activeIndex}
          >
            {chatCase.label}
          </button>
        ))}
      </div>

      <div className="maia-carousel relative mx-auto h-[26.5rem] w-full max-w-md sm:h-[27.5rem]">
        {CHAT_CASES.map((chatCase, index) => {
          const offset = slideOffset(index, activeIndex, total);
          const abs = Math.abs(offset);
          if (abs > 1) return null;

          let positionClass = 'is-active';
          if (offset === -1) positionClass = 'is-prev';
          if (offset === 1) positionClass = 'is-next';

          return (
            <div
              key={chatCase.id}
              className={`maia-carousel-slide ${positionClass}`}
              aria-hidden={offset !== 0}
            >
              <ChatMock
                chatCase={chatCase}
                visibleCount={offset === 0 ? visibleCount : chatCase.messages.length}
                revealing={offset === 0 && !paused}
              />
            </div>
          );
        })}

        <button
          type="button"
          className="maia-carousel-nav maia-carousel-nav-prev"
          aria-label="Case anterior"
          onClick={() => goTo(activeIndex - 1)}
        >
          ‹
        </button>
        <button
          type="button"
          className="maia-carousel-nav maia-carousel-nav-next"
          aria-label="Próximo case"
          onClick={() => goTo(activeIndex + 1)}
        >
          ›
        </button>
      </div>

      <div className="maia-carousel-progress mx-auto mt-4 max-w-md" aria-hidden>
        <div
          className="maia-carousel-progress-bar"
          style={{
            transform: `scaleX(${
              visibleCount < activeCase.messages.length
                ? visibleCount / activeCase.messages.length
                : progress
            })`,
          }}
        />
      </div>
    </div>
  );
}

function useLandingMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;

    const managedElements = META_TAGS.map(([tagName, attributes]) => {
      const selector =
        tagName === 'meta'
          ? attributes.name
            ? `meta[name="${attributes.name}"]`
            : `meta[property="${attributes.property}"]`
          : `link[rel="${attributes.rel}"]${attributes.type ? `[type="${attributes.type}"]` : ''}`;
      const existing = document.head.querySelector(selector);
      const element = existing || document.createElement(tagName);
      const previousAttributes = existing
        ? Array.from(existing.attributes).map(({ name, value }) => [name, value])
        : [];
      const removeOnCleanup = !existing || existing.dataset.maiaStatic === 'true';

      element.dataset.maiaSeo = 'true';
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      if (!existing) document.head.appendChild(element);

      return { element, previousAttributes, removeOnCleanup };
    });

    document.head.querySelectorAll('script[data-maia-static="true"]').forEach((item) => {
      item.remove();
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.maiaSeo = 'true';
    script.text = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);

    return () => {
      document.title = previousTitle === SEO.title ? MARKETING_NAME : previousTitle;
      managedElements.forEach(({ element, previousAttributes, removeOnCleanup }) => {
        if (removeOnCleanup) {
          element.remove();
          return;
        }
        Array.from(element.attributes).forEach(({ name }) => element.removeAttribute(name));
        previousAttributes.forEach(([name, value]) => element.setAttribute(name, value));
      });
      script.remove();
    };
  }, []);
}

function MaiaLanding({ prerender = false }) {
  const { isAuthenticated } = useAuth();

  useLandingMetadata();

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="relative overflow-hidden px-4 pb-24 pt-20 sm:px-6 sm:pb-32 sm:pt-28 lg:px-8">
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2 lg:gap-16">
          <div className="text-center lg:text-left">
            <p className="maia-rise font-display text-5xl text-[var(--maia-ink)] sm:text-6xl lg:text-7xl">
              {MARKETING_NAME}
            </p>
            <h1 className="maia-rise maia-rise-delay-1 mt-6 font-display text-3xl leading-[1.14] text-[var(--maia-ink)] text-balance sm:text-4xl lg:text-[3rem]">
              <Headline emphasis={HERO.headlineEmphasis}>{HERO.headline}</Headline>
            </h1>
            <p className="maia-rise maia-rise-delay-2 mx-auto mt-6 max-w-lg text-base leading-relaxed text-[var(--maia-muted)] text-pretty sm:text-lg lg:mx-0">
              {HERO.body}
            </p>
            <div className="maia-rise maia-rise-delay-3 mt-10">
              <LandingCtas isAuthenticated={isAuthenticated} align="start" />
            </div>
          </div>

          <div className="maia-rise maia-rise-delay-2 relative">
            <CaseShowcase revealAllMessages={prerender} />
          </div>
        </div>
      </section>

      <section id="produto" className="border-t border-[var(--maia-line)] bg-[var(--maia-paper-2)]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--maia-accent)]">
            {PROBLEM.eyebrow}
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl leading-[1.15] text-[var(--maia-ink)] text-balance sm:text-5xl">
            <Headline emphasis={PROBLEM.headlineEmphasis}>{PROBLEM.headline}</Headline>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[var(--maia-muted)] sm:text-lg">
            {PROBLEM.body}
          </p>

          <div className="mt-16 space-y-16 sm:mt-20 sm:space-y-24">
            {PROBLEM.reasons.map((reason) => (
              <div key={reason.eyebrow} className="grid items-start gap-6 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--maia-faint)]">
                    {reason.eyebrow}
                  </p>
                  <h3 className="mt-3 font-display text-3xl leading-[1.2] text-[var(--maia-ink)] text-balance sm:text-4xl">
                    <Headline emphasis={reason.headlineEmphasis}>{reason.headline}</Headline>
                  </h3>
                </div>
                <p className="text-base leading-relaxed text-[var(--maia-muted)] text-pretty md:col-span-7 md:pt-8 sm:text-lg">
                  {reason.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="border-t border-[var(--maia-line)]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--maia-accent)]">
              {DEFINITION.eyebrow}
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.15] text-[var(--maia-ink)] text-balance sm:text-5xl">
              <Headline emphasis={DEFINITION.headlineEmphasis}>{DEFINITION.headline}</Headline>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[var(--maia-muted)] sm:text-lg">
              {DEFINITION.body}
            </p>
          </div>

          <div className="mt-14 grid gap-8 border-t border-[var(--maia-line)] pt-12 sm:grid-cols-3 sm:gap-10">
            {DEFINITION.highlights.map((item) => (
              <article key={item.title}>
                <h3 className="text-lg font-semibold text-[var(--maia-ink)]">{item.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-[var(--maia-muted)]">
                  {item.description}
                </p>
              </article>
            ))}
          </div>

          <h2 className="mt-20 font-display text-3xl text-[var(--maia-ink)]">
            {STEPS_SECTION.headline}
          </h2>
          <ol className="mt-16 grid gap-12 border-t border-[var(--maia-line)] pt-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((step) => (
              <li key={step.number}>
                <span className="font-display text-5xl text-[var(--maia-accent)] opacity-80">
                  {step.number}
                </span>
                <h3 className="mt-4 text-lg font-semibold text-[var(--maia-ink)]">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--maia-muted)] sm:text-base">
                  {step.description}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="oferta" className="border-t border-[var(--maia-line)] bg-[var(--maia-paper-2)]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--maia-accent)]">
              {RESPONSIBILITIES_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 font-display text-4xl leading-[1.15] text-[var(--maia-ink)] text-balance sm:text-5xl">
              <Headline emphasis={RESPONSIBILITIES_SECTION.headlineEmphasis}>
                {RESPONSIBILITIES_SECTION.headline}
              </Headline>
            </h2>
          </div>

          <div className="mt-14 grid gap-10 border-t border-[var(--maia-line)] pt-12 md:grid-cols-2 md:gap-16">
            {RESPONSIBILITIES.map((group) => (
              <div key={group.label}>
                <h3 className="font-display text-3xl text-[var(--maia-ink)]">{group.label}</h3>
                <ul className="mt-6 space-y-3 text-base leading-relaxed text-[var(--maia-muted)]">
                  {group.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="text-[var(--maia-accent)]" aria-hidden>
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="mt-12 max-w-2xl text-base leading-relaxed text-[var(--maia-muted)]">
            {RESPONSIBILITIES_SECTION.footnote}
          </p>
        </div>
      </section>

      <section className="border-t border-[var(--maia-line)]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--maia-accent)]">
            {DIFFERENTIATORS_SECTION.eyebrow}
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl leading-[1.15] text-[var(--maia-ink)] text-balance sm:text-5xl">
            <Headline emphasis={DIFFERENTIATORS_SECTION.headlineEmphasis}>
              {DIFFERENTIATORS_SECTION.headline}
            </Headline>
          </h2>
          <div className="mt-14 grid gap-10 border-t border-[var(--maia-line)] pt-12 sm:grid-cols-2">
            {DIFFERENTIATORS.map((item) => (
              <article key={item.title}>
                <h3 className="text-lg font-semibold text-[var(--maia-ink)]">{item.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-[var(--maia-muted)]">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="duvidas" className="border-t border-[var(--maia-line)] bg-[var(--maia-paper-2)]">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--maia-accent)]">
            {FAQ_SECTION.eyebrow}
          </p>
          <h2 className="mt-4 font-display text-4xl text-[var(--maia-ink)] sm:text-5xl">
            {FAQ_SECTION.headline}
          </h2>
          <div className="mt-12 divide-y divide-[var(--maia-line)] border-y border-[var(--maia-line)]">
            {FAQS.map((item) => (
              <article key={item.question} className="py-7">
                <h3 className="text-lg font-semibold text-[var(--maia-ink)]">{item.question}</h3>
                <p className="mt-3 text-base leading-relaxed text-[var(--maia-muted)]">
                  {item.answer}
                </p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-xs text-[var(--maia-faint)]">
            {`Conteúdo atualizado em ${LAST_MODIFIED_LABEL}.`}
          </p>
        </div>
      </section>

      <section className="border-t border-[var(--maia-line)]">
        <div className="mx-auto max-w-3xl px-4 py-28 text-center sm:px-6 sm:py-36 lg:px-8">
          <h2 className="font-display text-4xl leading-[1.15] text-[var(--maia-ink)] text-balance sm:text-5xl">
            <Headline emphasis={CLOSING.headlineEmphasis}>{CLOSING.headline}</Headline>
          </h2>
          <p className="mx-auto mt-6 max-w-md text-base text-[var(--maia-muted)] sm:text-lg">
            {CLOSING.body}
          </p>
          <div className="mt-12">
            <LandingCtas isAuthenticated={isAuthenticated} />
          </div>
          <nav
            aria-label="Conheça outras soluções da MAIA"
            className="mx-auto mt-12 grid max-w-2xl gap-3 text-left sm:grid-cols-2"
          >
            {RELATED_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-2xl border border-[var(--maia-line)] bg-white/60 p-5 transition-colors hover:border-[var(--maia-accent)]"
              >
                <span className="block text-sm font-semibold text-[var(--maia-ink)]">
                  {link.label}
                </span>
                <span className="mt-2 block text-sm leading-relaxed text-[var(--maia-muted)]">
                  {link.description}
                </span>
              </a>
            ))}
          </nav>
        </div>
      </section>
    </div>
  );
}

export default MaiaLanding;
